import React, { useEffect, useState } from 'react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { Check, FileText, Zap } from 'lucide-react';
import { getNodeTypeColor } from '../workspace/types';

const TOTAL_STEPS = 4;
const STEP_DELAY_MS = 1100;
// Short first tick: just enough for React to commit the opacity-0 baseline so
// the CSS transition actually animates instead of snapping in.
const INITIAL_DELAY_MS = 600;


// Cascade: column N becomes visible when revealedCount > N (0-indexed). The
// final tick (revealedCount === TOTAL_STEPS + 1) ensures everything is on-
// screen and any "in-progress" emphasis is dropped. Plays every time the
// panel mounts. Skips entirely on reduced-motion.
function useStaggeredReveal() {
  const reducedMotion = usePrefersReducedMotion();
  const [revealedCount, setRevealedCount] = useState(
    reducedMotion ? TOTAL_STEPS + 1 : 0,
  );

  useEffect(() => {
    if (reducedMotion) return;
    if (revealedCount > TOTAL_STEPS) return;
    const delay = revealedCount === 0 ? INITIAL_DELAY_MS : STEP_DELAY_MS;
    const t = setTimeout(() => setRevealedCount((n) => n + 1), delay);
    return () => clearTimeout(t);
  }, [revealedCount, reducedMotion]);

  const skipAnimation = () => setRevealedCount(TOTAL_STEPS + 1);
  return { revealedCount, skipAnimation, reducedMotion };
}

/** One tutorial step: title + the static mock visual. Consumed by this
 *  panel's 4-column grid AND by the start page's one-step-at-a-time
 *  carousel (BAR-538), so the tutorial content has a single source. */
export interface TutorialStep {
  title: string;
  mock: React.ReactNode;
  /** Optional tighter visual for the start-page carousel (BAR-538) when the
   *  full mock is too tall for a one-step-at-a-time card. Falls back to mock. */
  carouselMock?: React.ReactNode;
}

export const RESEARCH_TUTORIAL_STEPS: TutorialStep[] = [
  { title: 'Ask your research question', mock: <ResearchComposerPreview /> },
  {
    title: 'BARK clarifies the scope with you',
    mock: (
      <div className="flex flex-col gap-2.5">
        <ClarifyingReplyPreview />
        <LightResearchCallout />
      </div>
    ),
  },
  {
    title: 'Review the research proposal',
    mock: (
      <div className="flex flex-col gap-2.5">
        <p className="text-[11px] leading-relaxed text-fg-secondary">
          BARK lays out a thorough proposal — hypothesis, scope, sources, and approach.
        </p>
        <ResearchProposalPreview />
        <p className="text-[11px] leading-relaxed text-fg-secondary">
          Tweak it or hit <span className="font-semibold text-fg">Confirm</span> and a multi-model investigation runs — every finding back-tracked to its source.
        </p>
      </div>
    ),
    carouselMock: <ResearchProposalPreview compact />,
  },
  { title: 'You get findings and a cited brief', mock: <ResearchDeliverablesPreview /> },
];

export default function ResearchWelcomePanel() {
  const { revealedCount, skipAnimation, reducedMotion } = useStaggeredReveal();
  const showAll = revealedCount > TOTAL_STEPS;

  return (
    <div className="w-full h-full bg-bg-warm overflow-auto">
      {/* pt-20 (vs. py-10) gives the floating Report/Graph tab pill above
          the workspace clear breathing room over the heading — the pill
          sits at top-3 in WorkspaceTabs, ~36px tall, so we need >56px of
          top space before the title to avoid visual collision. */}
      <div className="mx-auto max-w-[1180px] px-8 pt-20 pb-10">
        <header className="text-center mb-10">
          <h1 className="font-heading text-[40px] leading-[1.15] text-fg tracking-[-0.02em]">
            Welcome to Research
          </h1>
          <p className="mt-2 text-[14px] text-fg-secondary">
            Ask a regulatory question. BARK scopes it with you, then investigates.
          </p>
          {!reducedMotion && (
            <button
              type="button"
              onClick={skipAnimation}
              aria-hidden={showAll}
              tabIndex={showAll ? -1 : 0}
              className={`mt-3 text-[11px] text-fg-tertiary hover:text-fg-secondary
                         underline decoration-dotted underline-offset-2
                         transition-opacity duration-300
                         ${showAll ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
            >
              Skip intro
            </button>
          )}
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {RESEARCH_TUTORIAL_STEPS.map((step, i) => (
            <Column
              key={step.title}
              index={i}
              revealedCount={revealedCount}
              number={i + 1}
              title={step.title}
              mock={step.mock}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function Column({
  index,
  revealedCount,
  number,
  title,
  mock,
}: {
  index: number;
  revealedCount: number;
  number: number;
  title: string;
  mock: React.ReactNode;
}) {
  const isVisible = revealedCount > index;
  const isCurrent = revealedCount === index + 1; // most-recently-revealed step
  return (
    <div
      className={`flex flex-col transition-all duration-700 ease-out
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}
    >
      <div className="flex items-start gap-3 mb-4">
        <span
          className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-md
                     font-mono text-[13px] font-semibold transition-colors duration-300
                     ${
                       isCurrent
                         ? 'bg-[color:var(--primary)] text-on-primary'
                         : 'bg-[color:var(--primary)]/10 text-[color:var(--primary)]'
                     }`}
          aria-hidden="true"
        >
          {number}
        </span>
        <h2 className="text-[14px] font-semibold leading-[1.35] text-fg pt-0.5">
          {title}
        </h2>
      </div>
      <div className="flex-1">{mock}</div>
    </div>
  );
}

// =============================================================================
// COLUMN 1 — chat composer preview
// Mirrors the real composer: rounded-xl card, textarea slot, bottom row with
// workspace indicator + context bar + send button. Send button is navy
// (research lane) instead of copper (build lane).
// =============================================================================

function ResearchComposerPreview() {
  return (
    <div
      className="bg-surface border border-border-default rounded-xl shadow-sm"
      aria-hidden="true"
    >
      <div className="px-3 pt-2.5 pb-1 text-[13px] text-fg leading-snug min-h-[34px]">
        What are the criteria for 100% risk weight on an ADC loan?
      </div>

      <div className="flex items-center justify-between px-3 pb-2 pt-1 border-t border-divider gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-1 text-[10px] text-fg-tertiary shrink-0">
            <img
              src="/Data/images/BARK_tree_2_navy.png"
              alt=""
              className="w-3 h-3 object-contain"
            />
            <span className="font-medium">Research</span>
          </div>
          <div className="flex items-center gap-1 text-[10px] min-w-0">
            <span className="text-fg-tertiary">Context:</span>
            <div className="h-[3px] w-8 bg-bg-warm rounded-full overflow-hidden">
              <div className="h-full bg-success" style={{ width: '0%' }} />
            </div>
            <span className="font-medium text-success whitespace-nowrap">0k/200k</span>
          </div>
        </div>

        <span
          className="inline-flex items-center justify-center p-1 rounded
                     bg-[color:var(--primary)] text-on-primary shrink-0"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22,2 15,22 11,13 2,9" />
          </svg>
        </span>
      </div>
    </div>
  );
}

// =============================================================================
// COLUMN 2 — BARK clarifying reply
// Mini chat-message card. Lead-in line + four bullet questions about
// institution profile + a fallback offer line. Content lifted from the live
// system's response to the column-1 question.
// =============================================================================

function ClarifyingReplyPreview() {
  const bullets = [
    'Standardised approach or IRB?',
    'Residential or commercial ADC?',
    'EU baseline, or national overlays?',
    'Defaulted exposures in scope?',
  ];
  return (
    <div
      className="rounded-lg border border-border-default bg-surface p-3 shadow-bark-xs"
      aria-hidden="true"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-fg-secondary mb-1.5">
        BARK
      </div>
      <p className="text-[11px] leading-relaxed text-fg mb-2">
        ADC RW depends on which path applies. Couple of things to nail down:
      </p>
      <ul className="flex flex-col gap-1 mb-2">
        {bullets.map((b) => (
          <li
            key={b}
            className="flex items-start gap-1.5 text-[11px] leading-tight text-fg-secondary"
          >
            <span className="text-fg-tertiary leading-none mt-1">•</span>
            <span>{b}</span>
          </li>
        ))}
      </ul>
      <p className="text-[10px] leading-relaxed text-fg-tertiary italic">
        Or just hit me with the EU baseline.
      </p>
    </div>
  );
}

// Optional callout under column 2 — surfaces that BARK doesn't always go
// through the full proposal flow. Simpler questions trigger Light Research
// (depth="light" report path in research_workspace prompt), which skips the
// proposal card and returns a fast cited answer directly. Slate-accented
// callout to read as a "by the way" / "alternative path" rather than the
// primary flow.
function LightResearchCallout() {
  return (
    <div
      className="rounded-md bg-surface border border-border-default
                 border-l-[3px] border-l-fg-tertiary p-2.5 flex items-start gap-2"
      aria-hidden="true"
    >
      <Zap className="w-3.5 h-3.5 text-fg-tertiary shrink-0 mt-px" strokeWidth={1.8} />
      <div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.06em] text-fg-secondary mb-0.5">
          Light research
        </div>
        <p className="text-[11px] leading-relaxed text-fg-secondary">
          Not every question needs a full proposal. Simpler ones get a fast, cited answer from light research instead.
        </p>
      </div>
    </div>
  );
}

// =============================================================================
// COLUMN 3 — research proposal preview
// Mini replica of ResearchProposalCard (ChatMessageMarkdown.tsx:186-218):
// 3px navy left stripe, "RESEARCH PROPOSAL" eyebrow, compressed sections from
// the live system's proposal output (working hypothesis with UNCONFIRMED tag,
// regulatory landscape L1/L2/Nat, 5-item investigation plan, action pills).
// =============================================================================

// `compact` drops the investigation-plan list — used by the start-page
// carousel where the stage height is fixed (BAR-538); the workspace
// tutorial always renders the full card.
function ResearchProposalPreview({ compact = false }: { compact?: boolean } = {}) {
  const landscape: Array<[string, string]> = [
    ['CRR', 'Art 126a, 127'],
    ['CRD', 'Art 74, 79'],
    ['EBA', 'GL/2025/03'],
  ];
  const plan = [
    'Art 126a preconditions',
    'Path (a): pre-sale / pre-lease',
    'Path (b): obligor equity',
    'EBA GL numerical thresholds',
    'Art 127 default override',
  ];
  return (
    <div
      className="rounded-lg bg-bg-warm pl-4 pr-3 py-3 shadow-bark-xs
                 border-y border-r border-border-default
                 border-l-[3px] border-l-[color:var(--primary)]"
      aria-hidden="true"
    >
      <div className="flex flex-wrap items-baseline gap-x-2 mb-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-[color:var(--primary)]">
          Research proposal
        </span>
      </div>

      <SectionLabel>
        Working hypothesis
        <span className="ml-1 inline-block px-1 py-px text-[8px] font-mono uppercase tracking-wider bg-fg-tertiary/10 text-fg-tertiary rounded align-middle">
          UNCONFIRMED
        </span>
      </SectionLabel>
      <p className="text-[10px] leading-snug text-fg mb-2.5">
        100% RW for ADC is a narrow carve-out under Art 126a. Default is 150% — needs residential ADC, sound origination, and one of two alternative paths.
      </p>

      <SectionLabel>Regulatory landscape</SectionLabel>
      <ul className="text-[10px] leading-tight text-fg-secondary mb-2.5 space-y-0.5">
        {landscape.map(([level, text]) => (
          <li key={level} className="flex gap-1.5">
            <span className="font-mono text-fg-tertiary w-6 shrink-0">{level}</span>
            <span>{text}</span>
          </li>
        ))}
      </ul>

      {!compact && (
        <>
          <SectionLabel>Investigation plan</SectionLabel>
          <ol className="text-[10px] leading-tight text-fg-secondary mb-2.5 space-y-0.5">
            {plan.map((step, i) => (
              <li key={step} className="flex gap-1.5">
                <span className="font-mono text-fg-tertiary w-3 shrink-0">{i + 1}</span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </>
      )}

      <div className="flex items-center gap-1.5 pt-1">
        <span className="px-2 py-0.5 text-[10px] font-medium text-fg-secondary bg-surface border border-border-default rounded">
          Narrow
        </span>
        <span className="px-2 py-0.5 text-[10px] font-medium text-on-primary bg-[color:var(--primary)] rounded">
          Confirm — run it
        </span>
      </div>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[9px] font-semibold uppercase tracking-[0.06em] text-fg-secondary mb-1">
      {children}
    </div>
  );
}

// =============================================================================
// COLUMN 4 — deliverables strip
// Three tiles in a row with a green ✓ badge bottom-right (matches build's
// DeliverablesPreview composition). Each tile is a small content preview, not
// a generic icon: Report shows serif title + Direct Answer body with inline
// citation chips; Reasoning graph renders a real hub-and-spoke mini-DAG using
// getNodeTypeColor() hexes from the live workspace types; Findings shows a
// stack of finding rows with mono Article-reference chips.
// =============================================================================

function ResearchDeliverablesPreview() {
  return (
    <div className="relative rounded-lg p-1" aria-hidden="true">
      <div className="grid grid-cols-3 gap-2">
        <ReportTile />
        <GraphTile />
        <FindingsTile />
      </div>
      <span
        className="absolute -bottom-2 -right-2 inline-flex items-center justify-center
                   w-7 h-7 rounded-full bg-success text-white shadow-bark-sm border-2 border-bg-warm"
      >
        <Check className="w-3.5 h-3.5" strokeWidth={3} />
      </span>
    </div>
  );
}

function TileFrame({ children, label }: { children: React.ReactNode; label: string }) {
  return (
    <div
      className="flex flex-col rounded-md border border-border-default bg-surface
                 px-2 pt-2 pb-1.5 shadow-bark-xs min-h-[120px]"
    >
      <div className="flex-1 overflow-hidden">{children}</div>
      <span className="mt-1 text-[9px] font-medium text-fg-secondary text-center">
        {label}
      </span>
    </div>
  );
}

function ReportTile() {
  return (
    <TileFrame label="Report">
      <div className="font-heading text-[8px] leading-tight text-fg mb-1">
        ADC 100% risk weight — criteria
      </div>
      <div className="text-[7px] font-bold uppercase tracking-wider text-fg-secondary mb-0.5">
        Direct answer
      </div>
      <p className="text-[7px] leading-snug text-fg-secondary">
        Default for ADC is 150% RW. Art 126a is a narrow carve-out
        <CitationChip>1</CitationChip> — residential + sound origination
        <CitationChip>3</CitationChip> + one of two paths
        <CitationChip>5</CitationChip>
      </p>
    </TileFrame>
  );
}

function CitationChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-block mx-0.5 px-1 text-[6px] font-mono text-link bg-link-bg rounded align-baseline">
      {children}
    </span>
  );
}

// Mini hub-and-spoke DAG: 1 question (top, purple) → 4 reasoning/open-endpoint
// nodes (middle row, sky blue + slate) → 1 conclusion (bottom, amber). Edges
// are thin grey direct paths fanning out and reconverging. Pure SVG so it
// scales cleanly inside a small tile.
function GraphTile() {
  const reasoningXs = [12, 36, 60, 84];
  const yMid = 28;
  const questionColor = getNodeTypeColor('question');
  const reasoningColor = getNodeTypeColor('reasoning');
  const openEndpointColor = getNodeTypeColor('open_endpoint');
  const conclusionColor = getNodeTypeColor('conclusion');
  const edgeColor = '#cbd5e1';

  return (
    <TileFrame label="Reasoning graph">
      <svg
        viewBox="0 0 100 56"
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        {reasoningXs.map((x, i) => (
          <React.Fragment key={`e-${i}`}>
            <line x1="50" y1="9" x2={x} y2={yMid - 3} stroke={edgeColor} strokeWidth="0.4" />
            <line x1={x} y1={yMid + 3} x2="50" y2="47" stroke={edgeColor} strokeWidth="0.4" />
          </React.Fragment>
        ))}

        <MiniDAGNode x={40} y={2} w={20} h={7} color={questionColor} />
        {reasoningXs.map((x, i) => {
          const isOpenEndpoint = i === 3;
          const color = isOpenEndpoint ? openEndpointColor : reasoningColor;
          return (
            <MiniDAGNode key={`n-${i}`} x={x - 6} y={yMid - 3} w={12} h={6} color={color} />
          );
        })}
        <MiniDAGNode x={40} y={47} w={20} h={7} color={conclusionColor} />
      </svg>
    </TileFrame>
  );
}

function MiniDAGNode({
  x,
  y,
  w,
  h,
  color,
}: {
  x: number;
  y: number;
  w: number;
  h: number;
  color: string;
}) {
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="0.8" fill="white" stroke={color} strokeWidth="0.4" />
      <rect x={x} y={y} width={w} height={2} fill={color} />
    </g>
  );
}

function FindingsTile() {
  const findings: Array<[string, string]> = [
    ['Art 126a', 'Carve-out conditions'],
    ['Art 126a(2)(a)', 'Pre-sale path'],
    ['Art 126a(2)(b)', 'Equity path'],
    ['EBA GL/25/03', 'Numerical thresholds'],
    ['Art 127', 'Default override'],
  ];
  // Findings icon mirrors the live FindingsToggleButton (UnifiedFindingsPanel.tsx:201-213):
  // FileText with a small numeric badge top-right. Introduces the icon users will see
  // on the live workspace toggle.
  return (
    <TileFrame label="Findings">
      <div className="flex items-start gap-1.5 mb-1">
        <span className="relative inline-flex items-center justify-center text-fg-secondary shrink-0">
          <FileText className="w-3 h-3" strokeWidth={1.8} />
          <span className="absolute -top-1 -right-1.5 inline-flex items-center justify-center min-w-[10px] h-2.5 px-0.5 rounded-full bg-accent text-white text-[6px] font-medium leading-none">
            {findings.length}
          </span>
        </span>
        <ul className="flex flex-col gap-0.5 flex-1 min-w-0">
          {findings.map(([article, text]) => (
            <li key={article} className="flex items-center gap-1">
              <span className="font-mono text-[6px] leading-none text-link bg-link-bg px-1 py-0.5 rounded shrink-0">
                {article}
              </span>
              <span className="text-[7px] text-fg-secondary truncate">{text}</span>
            </li>
          ))}
        </ul>
      </div>
    </TileFrame>
  );
}
